<script>
import Title from '@/components/admin/common/TitleArea.vue'
import PageGuideDialog from '@/components/admin/fulfillment/guide/PageGuideDialog.vue'
import FulfillmentPageGuide from '@/components/admin/fulfillment/guide/FulfillmentPageGuide.vue'

const emptyForm = () => ({
  id: null,
  partnerId: null,
  brandId: null,
  categoryId: null,
  isActive: true,
  memo: ''
})

export default {
  name: 'PackingExpressRuleManage',
  components: { Title, PageGuideDialog, FulfillmentPageGuide },
  data() {
    return {
      description: { title: '익스프레스 규칙 관리' },
      rows: [],
      brands: [],
      categories: [],
      loading: false,
      saving: false,
      dialog: false,
      guideDialog: false,
      form: emptyForm(),
      filters: {
        partnerId: null,
        brandId: null,
        categoryId: null,
        isActive: ''
      },
      activeOptions: [
        { label: '전체', value: '' },
        { label: '활성', value: '1' },
        { label: '비활성', value: '0' }
      ],
      columns: [
        { name: 'partner', label: '파트너', field: 'partnerId', align: 'left' },
        { name: 'brand', label: '브랜드 범위', field: 'brandName', align: 'left' },
        { name: 'category', label: '카테고리 범위', field: 'categoryName', align: 'left' },
        { name: 'memo', label: '운영 메모', field: 'memo', align: 'left' },
        { name: 'isActive', label: '상태', field: 'isActive', align: 'center' },
        { name: 'updatedAt', label: '수정일', field: 'updatedAt', align: 'left' },
        { name: 'actions', label: '관리', field: 'actions', align: 'center' }
      ],
      pagination: { rowsPerPage: 20 }
    }
  },
  computed: {
    rawPartnerList() {
      return this.$store.state.storage?.partnerList || []
    },
    partnerOptions() {
      return this.rawPartnerList
        .filter(item => Number(item?.has_fulfillment_contract) === 1)
        .map(item => ({ label: item.partner_name, value: Number(item.id) }))
        .sort((a, b) => a.label.localeCompare(b.label, 'ko'))
    },
    brandOptions() {
      return this.brands.map(item => ({
        label: `${item.name}${Number(item.is_active) === 1 ? '' : ' (비활성)'}`,
        value: Number(item.brand_id)
      }))
    },
    categoryOptions() {
      return this.categories.map(item => ({
        label: `${item.name}${Number(item.is_active) === 1 ? '' : ' (비활성)'}`,
        value: Number(item.category_id)
      }))
    },
    isEditing() {
      return Boolean(this.form.id)
    },
    isPartnerWide() {
      return !this.form.brandId && !this.form.categoryId
    }
  },
  methods: {
    partnerLabel(partnerId) {
      return this.partnerOptions.find(item => Number(item.value) === Number(partnerId))?.label || `파트너 #${partnerId}`
    },
    formatDate(value) {
      if (!value) return '-'
      return new Date(value).toLocaleString('ko-KR')
    },
    buildQuery() {
      const params = new URLSearchParams()
      Object.entries(this.filters).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') params.set(key, value)
      })
      return params.toString()
    },
    async loadReferenceData() {
      const [brandRes, categoryRes] = await Promise.all([
        this.$http({ method: 'GET', url: '/api/fulfillment/admin/brands', name: '[ 익스프레스 규칙 / 브랜드 조회 ]' }),
        this.$http({ method: 'GET', url: '/api/fulfillment/admin/categories', name: '[ 익스프레스 규칙 / 카테고리 조회 ]' })
      ])
      this.brands = Array.isArray(brandRes.data?.data) ? brandRes.data.data : []
      this.categories = Array.isArray(categoryRes.data?.data) ? categoryRes.data.data : []
    },
    async loadRules() {
      this.loading = true
      try {
        const query = this.buildQuery()
        const response = await this.$http({
          method: 'GET',
          url: `/api/fulfillment/admin/packing-express-rules${query ? `?${query}` : ''}`,
          name: '[ 익스프레스 규칙 / 목록 조회 ]'
        })
        this.rows = Array.isArray(response.data?.data) ? response.data.data : []
      } catch (error) {
        this.rows = []
        this.$q.notify({ type: 'negative', message: error?.response?.data?.message || '익스프레스 규칙 조회에 실패했습니다.' })
      } finally {
        this.loading = false
      }
    },
    openCreate() {
      this.form = emptyForm()
      this.form.partnerId = this.filters.partnerId || null
      this.dialog = true
    },
    openEdit(row) {
      this.form = {
        id: row.id,
        partnerId: Number(row.partnerId),
        brandId: row.brandId ? Number(row.brandId) : null,
        categoryId: row.categoryId ? Number(row.categoryId) : null,
        isActive: Number(row.isActive) === 1,
        memo: row.memo || ''
      }
      this.dialog = true
    },
    async save() {
      if (!this.form.partnerId) {
        this.$q.notify({ type: 'warning', message: '파트너를 선택해 주세요.' })
        return
      }
      if (this.isPartnerWide && this.form.isActive) {
        const confirmed = window.confirm('브랜드와 카테고리를 모두 비우면 이 파트너의 전체 상품이 익스프레스 대상이 됩니다. 계속할까요?')
        if (!confirmed) return
      }

      this.saving = true
      try {
        const payload = {
          partnerId: this.form.partnerId,
          brandId: this.form.brandId,
          categoryId: this.form.categoryId,
          isActive: this.form.isActive,
          memo: this.form.memo
        }
        await this.$http({
          method: this.isEditing ? 'PUT' : 'POST',
          url: this.isEditing
            ? `/api/fulfillment/admin/packing-express-rules/${this.form.id}`
            : '/api/fulfillment/admin/packing-express-rules',
          data: payload,
          name: '[ 익스프레스 규칙 / 저장 ]'
        })
        this.$q.notify({ type: 'positive', message: this.isEditing ? '규칙이 수정되었습니다.' : '규칙이 등록되었습니다.' })
        this.dialog = false
        await this.loadRules()
      } catch (error) {
        this.$q.notify({ type: 'negative', message: error?.response?.data?.message || '익스프레스 규칙 저장에 실패했습니다.' })
      } finally {
        this.saving = false
      }
    },
    async toggleActive(row) {
      const nextActive = Number(row.isActive) !== 1
      try {
        await this.$http({
          method: 'PUT',
          url: `/api/fulfillment/admin/packing-express-rules/${row.id}`,
          data: {
            partnerId: row.partnerId,
            brandId: row.brandId,
            categoryId: row.categoryId,
            isActive: nextActive,
            memo: row.memo
          },
          name: '[ 익스프레스 규칙 / 활성 상태 변경 ]'
        })
        this.$q.notify({ type: 'positive', message: nextActive ? '규칙이 활성화되었습니다.' : '규칙이 비활성화되었습니다.' })
        await this.loadRules()
      } catch (error) {
        this.$q.notify({ type: 'negative', message: error?.response?.data?.message || '상태 변경에 실패했습니다.' })
      }
    }
  },
  async mounted() {
    const routePartnerId = Number(this.$route.query.partnerId || 0)
    if (routePartnerId > 0) this.filters.partnerId = routePartnerId
    try {
      await this.loadReferenceData()
    } catch (error) {
      this.$q.notify({ type: 'negative', message: error?.response?.data?.message || '기준정보 조회에 실패했습니다.' })
    }
    await this.loadRules()
  }
}
</script>

<template>
  <q-page class="q-pa-lg">
    <div class="row q-mb-lg">
      <div class="col">
        <Title :desc="description">
          <template #title-action>
            <q-btn flat round dense size="sm" color="primary" icon="help_outline" aria-label="익스프레스 규칙 관리 사용 가이드" @click="guideDialog = true">
              <q-tooltip>익스프레스 규칙 관리 사용 가이드</q-tooltip>
            </q-btn>
          </template>
          <q-btn color="primary" icon="add" label="규칙 등록" @click="openCreate" />
        </Title>
      </div>
    </div>

    <q-banner rounded class="bg-blue-1 text-blue-10 q-mb-md">
      익스프레스 포장 허용 범위를 파트너별로 관리합니다. 브랜드·카테고리가 모두 비어 있으면 해당 파트너의 전체 상품이 허용됩니다.
    </q-banner>

    <div class="comp-box q-pa-md q-mb-md">
      <div class="row q-col-gutter-md items-end">
        <div class="col-12 col-md-3">
          <q-select v-model="filters.partnerId" :options="partnerOptions" emit-value map-options clearable outlined dense label="파트너" />
        </div>
        <div class="col-12 col-md-3">
          <q-select v-model="filters.brandId" :options="brandOptions" emit-value map-options clearable outlined dense label="브랜드" />
        </div>
        <div class="col-12 col-md-3">
          <q-select v-model="filters.categoryId" :options="categoryOptions" emit-value map-options clearable outlined dense label="카테고리" />
        </div>
        <div class="col-12 col-md-2">
          <q-select v-model="filters.isActive" :options="activeOptions" emit-value map-options outlined dense label="활성 여부" />
        </div>
        <div class="col-12 col-md-1">
          <q-btn color="primary" label="조회" class="full-width" @click="loadRules" />
        </div>
      </div>
    </div>

    <div class="comp-box">
      <q-table
        flat
        :rows="rows"
        :columns="columns"
        row-key="id"
        :loading="loading"
        v-model:pagination="pagination"
        no-data-label="등록된 익스프레스 규칙이 없습니다."
      >
        <template #body-cell-partner="props">
          <q-td :props="props">{{ partnerLabel(props.row.partnerId) }}</q-td>
        </template>
        <template #body-cell-brand="props">
          <q-td :props="props">{{ props.row.brandName || '전체 브랜드' }}</q-td>
        </template>
        <template #body-cell-category="props">
          <q-td :props="props">{{ props.row.categoryName || '전체 카테고리' }}</q-td>
        </template>
        <template #body-cell-isActive="props">
          <q-td :props="props">
            <q-badge :color="Number(props.value) === 1 ? 'positive' : 'grey-6'">
              {{ Number(props.value) === 1 ? '활성' : '비활성' }}
            </q-badge>
          </q-td>
        </template>
        <template #body-cell-updatedAt="props">
          <q-td :props="props">{{ formatDate(props.value) }}</q-td>
        </template>
        <template #body-cell-actions="props">
          <q-td :props="props">
            <q-btn flat dense color="primary" label="수정" @click="openEdit(props.row)" />
            <q-btn
              flat
              dense
              :color="Number(props.row.isActive) === 1 ? 'negative' : 'positive'"
              :label="Number(props.row.isActive) === 1 ? '비활성화' : '활성화'"
              @click="toggleActive(props.row)"
            />
          </q-td>
        </template>
      </q-table>
    </div>

    <PageGuideDialog
      v-model="guideDialog"
      title="익스프레스 규칙 관리 사용 가이드"
      description="포장 익스프레스 허용 범위를 안전하게 관리하는 방법입니다."
    >
      <FulfillmentPageGuide guide-key="packingExpressRules" />
    </PageGuideDialog>

    <q-dialog v-model="dialog" persistent>
      <q-card style="width: 640px; max-width: 95vw;">
        <q-card-section class="row items-center">
          <div class="text-h6">{{ isEditing ? '익스프레스 규칙 수정' : '익스프레스 규칙 등록' }}</div>
          <q-space />
          <q-btn v-close-popup flat round dense icon="close" />
        </q-card-section>
        <q-separator />
        <q-card-section class="q-gutter-md">
          <q-select v-model="form.partnerId" :options="partnerOptions" emit-value map-options outlined label="파트너 *" />
          <div class="row q-col-gutter-md">
            <div class="col-12 col-md-6">
              <q-select v-model="form.brandId" :options="brandOptions" emit-value map-options clearable outlined label="브랜드 (비우면 전체)" />
            </div>
            <div class="col-12 col-md-6">
              <q-select v-model="form.categoryId" :options="categoryOptions" emit-value map-options clearable outlined label="카테고리 (비우면 전체)" />
            </div>
          </div>
          <q-banner v-if="isPartnerWide" dense rounded class="bg-orange-1 text-orange-10">
            파트너 전체 상품 허용 규칙입니다. 오등록 시 대상 범위가 매우 넓어집니다.
          </q-banner>
          <q-input v-model="form.memo" outlined type="textarea" maxlength="255" counter label="운영 메모" />
          <q-toggle v-model="form.isActive" label="활성 규칙" />
        </q-card-section>
        <q-card-actions align="right">
          <q-btn v-close-popup flat label="취소" />
          <q-btn color="primary" label="저장" :loading="saving" @click="save" />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </q-page>
</template>
